import _ from 'lodash';

import { generateProcessConfigToken } from './service';
import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';

const api = bindServices('processConfig');

// ---------------------------------------------------------------------------
// generateAndStoreToken — shared generator: call service + dispatch queryGeneratedToken
// Returns the raw tokenResponse so callers can use versionId/token for further work.
// ---------------------------------------------------------------------------

function* generateAndStoreToken({ call, put }, processId, accessMode, extraPayload = {}) {
  const tokenResponse = yield call(generateProcessConfigToken, processId, accessMode);
  if (tokenResponse && tokenResponse.token) {
    yield put({
      type: 'queryGeneratedToken',
      payload: {
        token: tokenResponse.token,
        versionId: tokenResponse.versionId,
        versionNumber: tokenResponse.versionNumber,
        accessMode,
        processId,
        ...extraPayload,
      },
    });
  }
  return tokenResponse;
}

// ---------------------------------------------------------------------------
// bladeManager — pure functions for immutable blade array operations
// ---------------------------------------------------------------------------

const bladeManager = {
  // Upsert by key: replace if key exists, otherwise append
  upsert(blades, blade) {
    const idx = blades.findIndex((b) => b.key === blade.key);
    if (idx !== -1) {
      const next = [...blades];
      next[idx] = blade;
      return next;
    }
    return [...blades, blade];
  },

  // Move existing blade to end; if absent and addIfMissing=true, append it
  moveToEnd(blades, blade, addIfMissing = true) {
    const idx = blades.findIndex((b) => b.key === blade.key);
    if (idx >= 0) {
      const next = [...blades];
      const [existing] = next.splice(idx, 1);
      next.push(existing);
      return next;
    }
    return addIfMissing ? [...blades, blade] : blades;
  },

  // Replace the last blade
  replaceLast(blades, blade) {
    if (!blades.length) return blades;
    const next = [...blades];
    next[next.length - 1] = blade;
    return next;
  },

  // Update all blades that match predicate with updater fn
  updateWhere(blades, predicate, updater) {
    return blades.map((b) => (predicate(b) ? updater(b) : b));
  },

  // Remove last blade
  pop(blades) {
    return blades.slice(0, -1);
  },

  // Remove blade by key
  removeByKey(blades, key) {
    return blades.filter((b) => b.key !== key);
  },

  // Keep blades[0..fromIndex) — truncate from index onwards
  truncateFrom(blades, fromIndex) {
    return blades.slice(0, fromIndex);
  },
};

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

const Model = {
  namespace: 'processConfig',

  state: {
    id: null,
    searchConfig: '',
    processSearchCriteria: '',
    selectedKeys: '',
    processId: null,
    processData: null, // Store process details including name
    processVersions: [], // Store available process versions
    generatedToken: null,
    versionId: null, // Added versionId to state
    accessMode: null,
    listConfig: [],
    newPermission: false,
    searchConfigLoading: false,
    blades: [],
    overflowBlades: [], // Blades displaced by the 2-level cap, restored on close
    isSiderCollapsed: false,
    siderManuallyExpanded: false, // Track if user manually expanded when auto-collapsed
  },

  effects: {
    *fetchProcessConfig({ payload }, { call, put }) {
      try {
        const isProcessConfigContext = payload?.context === 'process-config';
        const skipSummary = Boolean(payload?.skipSummary) || !isProcessConfigContext;

        if (!skipSummary) {
          // First, fetch process details to get the name
          const processData = yield call(api.getById, { route: 'process', id: payload.processId });

          if (processData) {
            yield put({
              type: 'storeProcessData',
              payload: processData,
            });
          }

          // Fetch available versions
          const versionsResponse = yield call(api.apiRoute, {
            route: `process/config/${payload.processId}/versions`,
            params: { $select: 'id,number,current,published' },
          });

          if (versionsResponse && versionsResponse.items) {
            yield put({
              type: 'storeProcessVersions',
              payload: versionsResponse.items,
            });
          }
        }

        // Then, generate token for the process
        const tokenResponse = yield call(
          generateProcessConfigToken,
          payload.processId,
          CoreEnums.AccessMode.ReadOnly,
        );

        if (tokenResponse && tokenResponse.token) {
          // Store the token and versionId
          yield put({
            type: 'queryGeneratedToken',
            payload: {
              token: tokenResponse.token,
              versionId: tokenResponse.versionId,
              versionNumber: tokenResponse.versionNumber,
              accessMode: CoreEnums.AccessMode.ReadOnly,
              processId: payload.processId, // Add processId to the payload
            },
          });

          if (!skipSummary) {
            // Now fetch the summary using the token and versionId
            const summaryResponse = yield call(api.apiRoute, {
              route: `process/config/summary/${payload.processId}/${tokenResponse.versionId}`,
              params: { token: tokenResponse.token },
            });

            if (summaryResponse) {
              yield put({
                type: 'queryProcessConfig',
                payload: summaryResponse,
              });
            }
          }
        }
      } catch (error) {
        console.error('[processConfig/fetchProcessConfig]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *changeProcessVersion({ payload }, { call, put, select }) {
      try {
        const { versionNumber, processId } = payload;
        const currentState = yield select((state) => state.processConfig);
        const isProcessConfigContext = payload?.context === 'process-config';
        const skipSummary = Boolean(payload?.skipSummary) || !isProcessConfigContext;

        // Close all action-related blades when changing version
        const blades = currentState.blades || [];
        const filteredBlades = blades.filter(
          (blade) =>
            !blade.key.startsWith('action-') &&
            blade.key !== 'actions-root' &&
            blade.key !== 'actions-menu',
        );

        yield put({
          type: 'updateBlades',
          payload: filteredBlades,
        });

        // Generate token for the selected version
        const tokenResponse = yield call(
          generateProcessConfigToken,
          processId,
          CoreEnums.AccessMode.ReadOnly,
        );

        if (tokenResponse && tokenResponse.token) {
          yield put({
            type: 'queryGeneratedToken',
            payload: {
              token: tokenResponse.token,
              versionId: tokenResponse.versionId,
              versionNumber: tokenResponse.versionNumber,
              accessMode: CoreEnums.AccessMode.ReadOnly,
              processId: processId,
              // No reopenActionId - we always close blades when changing version
            },
          });

          // Clear the active action when changing version
          yield put({
            type: 'processActionConfig/clearActiveAction',
          });

          if (!skipSummary) {
            // Fetch the summary for the new version
            const summaryResponse = yield call(api.apiRoute, {
              route: `process/config/summary/${processId}/${tokenResponse.versionId}`,
              params: { token: tokenResponse.token },
            });

            if (summaryResponse) {
              yield put({
                type: 'queryProcessConfig',
                payload: summaryResponse,
              });
            }
          }
        }
      } catch (error) {
        console.error('[processConfig/changeProcessVersion]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *closeNewProcess({ payload }, { put }) {
      yield put({
        type: 'closeProcess',
        payload,
      });
    },

    *showItemProcesss({ view }, { put }) {
      yield put({
        type: 'showEditItemProcess',
        view,
      });
    },

    *processConfigFilter({ payload }, { put }) {
      yield put({
        type: 'searchConfig',
        payload,
      });
    },

    *processConfigFilterLoading({ payload = 'searchConfigLoading' }, { put }) {
      yield put({
        type: payload,
      });
    },

    *processSearch({ payload }, { put }) {
      yield put({
        type: 'processSearchCriteria',
        payload,
      });
    },

    *hideAllForms({ payload = 'hideForms' }, { put }) {
      yield put({ type: payload });
    },

    *resetConfig({ payload = 'resetConfigs' }, { put }) {
      yield put({ type: payload });
    },

    *selectedKey({ payload }, { put }) {
      yield put({
        type: 'selectedKeys',
        payload,
      });
    },

    *toggleAccessMode({ payload }, { call, put, select }) {
      try {
        const { processId } = payload;
        const currentState = yield select((state) => state.processConfig);
        const actionConfigState = yield select((state) => state.processActionConfig);
        const currentAccessMode = currentState.accessMode || CoreEnums.AccessMode.ReadOnly;

        const newAccessMode =
          currentAccessMode === CoreEnums.AccessMode.ReadOnly
            ? CoreEnums.AccessMode.Write
            : CoreEnums.AccessMode.ReadOnly;

        // Only reopen action when switching FROM ReadOnly TO Write
        const openActionId = actionConfigState.activeActionId;
        const extraPayload =
          openActionId && newAccessMode === CoreEnums.AccessMode.Write
            ? { reopenActionId: openActionId }
            : {};

        const tokenResponse = yield* generateAndStoreToken(
          { call, put },
          processId,
          newAccessMode,
          extraPayload,
        );

        if (tokenResponse && tokenResponse.token) {
          // Close all action-related blades to force refresh with new token
          const blades = currentState.blades || [];
          const filteredBlades = blades.filter(
            (blade) =>
              !blade.key.startsWith('action-') &&
              blade.key !== 'actions-root' &&
              blade.key !== 'actions-menu',
          );

          yield put({
            type: 'updateBlades',
            payload: filteredBlades,
          });

          // Refresh diagram with new token to ensure all components use the updated token
          const diagramResponse = yield call(api.apiRoute, {
            route: `process/config/${processId}/${tokenResponse.versionId}/diagram`,
            params: { token: tokenResponse.token },
          });

          if (diagramResponse) {
            yield put({
              type: 'processActionConfig/queryActionsFromDiagram',
              payload: diagramResponse,
            });
          }

          if (newAccessMode === CoreEnums.AccessMode.ReadOnly) {
            yield put({
              type: 'processActionConfig/clearActiveAction',
            });
          }
        }
      } catch (error) {
        console.error('[processConfig/toggleAccessMode]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *fetchWriteToken({ payload }, { call, put }) {
      try {
        const { processId } = payload;
        yield* generateAndStoreToken({ call, put }, processId, CoreEnums.AccessMode.Write);
      } catch (error) {
        console.error('[processConfig/fetchWriteToken]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    // Shared save for action sub-config forms; body = { elementType, itemId, data }, elementType routes it server-side.
    *modifyProcessConfig({ payload }, { call, put }) {
      try {
        const { processId, versionId, token, body } = payload;
        if (!processId || !versionId || !token) return;

        yield call(api.apiRoute, {
          route: `process/config/modify/${processId}/${versionId}`,
          method: 'post',
          params: { token },
          data: body,
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({
          type: 'processActionConfig/fetchDiagramByToken',
          payload: { processId, versionId, token },
        });
      } catch (error) {
        if (error && error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },
  },

  reducers: {
    // Upsert by key: replace existing blade at same key, otherwise append.
    // Enforces a max of 3 visible blades (sider + 2 content). When the cap is
    // reached, the current last blade is displaced into overflowBlades and the
    // new blade takes its slot. Closing that blade restores the displaced one.
    pushBlade(state, { payload }) {
      const MAX_VISIBLE_BLADES = 3;
      const currentBlades = state.blades || [];
      const overflowBlades = state.overflowBlades || [];

      // In-place replacement — array length unchanged, no overflow update needed
      const keyExists = currentBlades.some((b) => b.key === payload.key);
      if (keyExists) {
        return { ...state, blades: bladeManager.upsert(currentBlades, payload) };
      }

      // New blade that would exceed the cap — displace the last blade into overflow
      if (currentBlades.length >= MAX_VISIBLE_BLADES) {
        const displaced = currentBlades[currentBlades.length - 1];
        return {
          ...state,
          blades: bladeManager.replaceLast(currentBlades, payload),
          overflowBlades: [...overflowBlades, displaced],
        };
      }

      // Within limit — normal append
      return { ...state, blades: bladeManager.upsert(currentBlades, payload) };
    },

    pushDrawerBlade(state, { payload }) {
      const blades = state.blades || [];
      const overflowBlades = state.overflowBlades || [];
      const targetLevel = payload.level ?? 0;

      const nonActionBlades = blades.filter((b) => !b.key.startsWith('action-'));
      const filteredOverflow = overflowBlades.filter(
        (b) => b.level != null && b.level < targetLevel,
      );

      const parentChainFromBlades = blades
        .filter((b) => b.key.startsWith('action-'))
        .filter((b) => b.level != null && b.level < targetLevel);

      return {
        ...state,
        blades: [...nonActionBlades, payload],
        overflowBlades: [...filteredOverflow, ...parentChainFromBlades],
      };
    },

    // Clear to sider-only, then append the new blade
    navigateToBlade(state, { payload }) {
      const blades = state.blades || [];
      const nonSiderBlades = blades.filter((b) => b.key !== 'sider');
      if (nonSiderBlades.length === 1 && nonSiderBlades[0].key === payload.key) {
        return state;
      }
      const siderBlade = blades.find((b) => b.key === 'sider');
      const base = siderBlade ? [siderBlade] : [];
      return { ...state, blades: [...base, payload], overflowBlades: [] };
    },

    // Swap the last blade in the stack
    replaceTopBlade(state, { payload }) {
      return {
        ...state,
        blades: bladeManager.replaceLast(state.blades || [], payload),
        overflowBlades: [],
      };
    },

    // Set collapsed flag on all blades that have a collapsedWidth
    updateBladesCollapse(state, { payload }) {
      return {
        ...state,
        blades: bladeManager.updateWhere(
          state.blades || [],
          (b) => b && b.collapsedWidth != null,
          (b) => ({ ...b, collapsed: payload }),
        ),
      };
    },

    // Remove the last blade — restores the previously displaced blade if any
    popBlade(state) {
      const overflowBlades = state.overflowBlades || [];
      if (overflowBlades.length > 0) {
        const restored = overflowBlades[overflowBlades.length - 1];
        return {
          ...state,
          blades: bladeManager.replaceLast(state.blades || [], restored),
          overflowBlades: overflowBlades.slice(0, -1),
        };
      }
      return { ...state, blades: bladeManager.pop(state.blades || []) };
    },

    // Remove a blade by key
    removeBlade(state, { payload }) {
      return {
        ...state,
        blades: bladeManager.removeByKey(state.blades || [], payload.key),
        overflowBlades: [],
      };
    },

    // Remove the blade at fromIndex and everything after it.
    // When only the top blade is being closed and there is overflow, restore
    // the previously displaced blade instead of simply truncating.
    closeBladesFromIndex(state, { payload }) {
      const { fromIndex } = payload;
      const blades = state.blades || [];
      const overflowBlades = state.overflowBlades || [];

      if (fromIndex === blades.length - 1 && overflowBlades.length > 0) {
        const restored = overflowBlades[overflowBlades.length - 1];
        return {
          ...state,
          blades: bladeManager.replaceLast(blades, restored),
          overflowBlades: overflowBlades.slice(0, -1),
        };
      }

      return {
        ...state,
        blades: bladeManager.truncateFrom(blades, fromIndex),
        overflowBlades: [],
      };
    },

    navigateToDepth(state, { payload }) {
      const { source, idx } = payload;
      const blades = state.blades || [];
      const overflowBlades = state.overflowBlades || [];

      if (source === 'blades') {
        return {
          ...state,
          blades: bladeManager.truncateFrom(blades, idx + 1),
          overflowBlades: [],
        };
      }

      if (source === 'overflow') {
        const targetBlade = overflowBlades[idx];
        if (!targetBlade) return state;
        const newOverflow =
          targetBlade.level != null
            ? overflowBlades.filter((b) => b.level != null && b.level < targetBlade.level)
            : overflowBlades.slice(0, idx);

        return {
          ...state,
          blades: bladeManager.replaceLast(blades, targetBlade),
          overflowBlades: newOverflow,
        };
      }

      return state;
    },

    // Remove all action- blades then append the new one
    replaceActionBlade(state, { payload }) {
      const filtered = (state.blades || []).filter((b) => !b.key.startsWith('action-'));
      return { ...state, blades: [...filtered, payload], overflowBlades: [] };
    },

    // Remove the form blades for this specific action then append the new one
    replaceActionFormBlade(state, { payload }) {
      const { actionId, key } = payload;
      const filtered = (state.blades || []).filter(
        (b) => b.key !== key && !b.key.startsWith(`action-${actionId}-form`),
      );
      return { ...state, blades: [...filtered, payload], overflowBlades: [] };
    },

    // Append blade; if key already exists, move it to end instead
    addActionBlade(state, { payload }) {
      return {
        ...state,
        blades: bladeManager.moveToEnd(state.blades || [], payload, true),
        overflowBlades: [],
      };
    },

    // Move an existing blade to the end (no-op if not found)
    showActionBlade(state, { payload }) {
      const blades = state.blades || [];
      const idx = blades.findIndex((b) => b.key === payload);
      if (idx < 0) return state;
      const next = [...blades];
      const [existing] = next.splice(idx, 1);
      next.push(existing);
      return { ...state, blades: next, overflowBlades: [] };
    },

    // Update the title of a blade by key
    updateBladeTitle(state, { payload }) {
      const { key, title } = payload;
      return {
        ...state,
        blades: bladeManager.updateWhere(
          state.blades || [],
          (b) => b.key === key,
          (b) => ({ ...b, title }),
        ),
      };
    },

    trimOverflowAboveLevel(state, { payload }) {
      const { maxLevelExclusive } = payload;
      return {
        ...state,
        overflowBlades: (state.overflowBlades || []).filter(
          (b) => b.level != null && b.level < maxLevelExclusive,
        ),
      };
    },

    // Wholesale replace the blades array
    updateBlades(state, { payload }) {
      return { ...state, blades: payload, overflowBlades: [] };
    },

    clearReopenActionId(state) {
      return {
        ...state,
        generatedToken: {
          ...state.generatedToken,
          reopenActionId: null,
        },
      };
    },

    // Remove all action-related blades, then push actionsRootBlade (and optional actionFormBlade)
    handleActionClick(state, { payload }) {
      const { actionsRootBlade, actionFormBlade } = payload;
      let blades = (state.blades || []).filter(
        (b) =>
          b.key !== 'actions-root' &&
          b.key !== 'actions-menu' &&
          b.key !== 'add-action-form' &&
          !b.key.startsWith('action-'),
      );
      blades = [...blades, actionsRootBlade];
      if (actionFormBlade) blades = [...blades, actionFormBlade];
      return { ...state, blades, overflowBlades: [] };
    },

    // Reset blades to empty
    clearBlades(state) {
      return { ...state, blades: [], overflowBlades: [] };
    },

    setSiderCollapsed(state, { payload }) {
      // payload can be boolean or object { collapsed: boolean, manual: boolean }
      if (typeof payload === 'boolean') {
        return { ...state, isSiderCollapsed: payload };
      }

      const { collapsed, manual = false } = payload;
      return {
        ...state,
        isSiderCollapsed: collapsed,
        siderManuallyExpanded:
          manual && !collapsed ? true : manual ? false : state.siderManuallyExpanded,
      };
    },
    storeProcessData(state, { payload }) {
      return {
        ...state,
        processData: payload,
      };
    },

    storeProcessVersions(state, { payload }) {
      return {
        ...state,
        processVersions: payload,
      };
    },

    queryGeneratedToken(state, { payload }) {
      return {
        ...state,
        generatedToken: payload, // Store the entire payload object
        versionId: payload.versionId,
        accessMode: payload.accessMode,
        processId: payload.processId, // Add processId to state
      };
    },

    queryProcessConfig(state, { payload }) {
      return {
        ...state,
        listConfig: payload, // payload is now the summary response directly
        processId: state.processId,
      };
    },

    closeProcess(state, { payload }) {
      return {
        ...state,
        newProcess: payload.state,
        selectedItem: state.selectedItem,
        view: state.view,
        id: state.id,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    searchConfig(state, { payload }) {
      return {
        ...state,
        searchConfig: payload,
        searchConfigLoading: true,
      };
    },

    searchConfigLoading(state) {
      return {
        ...state,
        searchConfigLoading: false,
      };
    },

    processSearchCriteria(state, { payload }) {
      return {
        ...state,
        processSearchCriteria: payload,
      };
    },

    selectedKeys(state, { payload }) {
      return {
        ...state,
        selectedKeys: state.selectedKeys === payload ? '' : payload,
      };
    },

    resetConfigs(state) {
      return {
        ...state,
        searchConfig: '',
        searchConfigLoading: false,
        processSearchCriteria: '',
        selectedKeys: '',
        listConfig: [],
      };
    },

    resetConfig(state) {
      return {
        ...state,
        processId: null,
        processData: null,
        processVersions: [],
        generatedToken: null,
        versionId: null,
        accessMode: null,
        listConfig: [],
        blades: [],
        searchConfig: '',
        searchConfigLoading: false,
        processSearchCriteria: '',
        selectedKeys: '',
        newPermission: false,
        orderBy: '',
        id: null,
      };
    },

    hideForms(state) {
      return {
        ...state,
        id: null,
        searchConfig: '',
        processSearchCriteria: '',
        selectedKeys: '',
        processId: null,
      };
    },
  },
};

export default Model;
